import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { FontText } from './FontText';
import { AppleDownloadButton } from './AppleDownloadButton';
import { AndroidDownloadButton } from './AndroidDownloadButton';
import { localAnalytics } from '../../utils/analytics';

interface DownloadButtonsBannerProps {
  bookTitle?: string;
  chapterNumber?: number;
  source?: string;
  onShow?: () => void;
  scrollTrigger?: number;
}

export const DownloadButtonsBanner: React.FC<DownloadButtonsBannerProps> = ({
  bookTitle,
  chapterNumber,
  source = 'paywall_close',
  onShow,
  scrollTrigger = 0,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { userId } = useAuth();
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localAnalytics().logEvent('DownloadButtonsBannerShown', {
      screen: 'Chapter',
      action: 'DownloadBannerShown',
      userId,
      bookTitle,
      chapterNumber,
      source,
    });

    onShow?.();
  }, []);

  useEffect(() => {
    if (scrollTrigger > 0) {
      setTimeout(() => {
        if (bannerRef.current) {
          bannerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 50);
    }
  }, [scrollTrigger]);

  const additionalData = {
    bookTitle,
    chapterNumber,
    source,
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      marginBottom: 50,
      display: 'flex',
    },
    content: {
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 25,
    },
    appInfo: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      width: '100%',
    },
    appIcon: {
      width: 56,
      height: 56,
      borderRadius: 12,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
    textContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 20,
      width: '100%',
    },
    heading: {
      textAlign: 'center',
      maxWidth: 345,
      margin: 0,
    },
    textWrapper: {
      color: theme.colors.text,
    },
    freeText: {
      color: theme.colors.accent,
      fontWeight: 600,
    },
    subheading: {
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },
    downloadButtons: {
      display: 'flex',
      gap: 17,
      width: '100%',
      maxWidth: 345,
      justifyContent: 'center',
    },
  };

  return (
    <div ref={bannerRef} style={styles.container}>
      <div style={styles.content}>
        <div style={styles.appInfo}>
          <img src="/img/favicon.png" alt="App Icon" style={styles.appIcon} />
          <div style={styles.textContent}>
            <div style={styles.heading}>
              <FontText h2 style={styles.textWrapper}>
                {t('read_free_chapters')}{' '}
              </FontText>
              <FontText h2 style={styles.freeText}>
                {t('free_chapters')}
              </FontText>
              <FontText h2 style={styles.textWrapper}>
                {' '}
                {t('every_8_hours')}
              </FontText>
            </div>
            <FontText normal style={styles.subheading}>
              {t('download_nanoreads_app')}
            </FontText>
          </div>
        </div>
        <div style={styles.downloadButtons}>
          <AppleDownloadButton
            userId={userId}
            additionalData={additionalData}
            source={source}
          />
          <AndroidDownloadButton
            userId={userId}
            additionalData={additionalData}
            source={source}
          />
        </div>
      </div>
    </div>
  );
};
